import React, { useRef, useEffect } from 'react';
import PropertyCard from './PropertyCard';

export default function AutoScrollCarousel({ items }) {
  const trackRef = useRef(null);
  const animRef = useRef(null);
  const pausedRef = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollStart = useRef(0);
  const SPEED = 0.5;

  useEffect(() => {
    const track = trackRef.current;
    if (!track || items.length === 0) return;

    const tick = () => {
      if (!pausedRef.current && !isDragging.current) {
        track.scrollLeft += SPEED;
        // Seamless: when we've scrolled one full set, jump back by exactly one set
        const half = track.scrollWidth / 2;
        if (track.scrollLeft >= half) {
          track.scrollLeft -= half;
        }
      }
      animRef.current = requestAnimationFrame(tick);
    };

    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [items]);

  const onMouseEnter = () => { pausedRef.current = true; };
  const onMouseLeave = () => {
    pausedRef.current = false;
    isDragging.current = false;
    if (trackRef.current) trackRef.current.style.cursor = 'default';
  };

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX;
    scrollStart.current = trackRef.current.scrollLeft;
    trackRef.current.style.cursor = 'grabbing';
  };

  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const delta = startX.current - e.pageX;
    trackRef.current.scrollLeft = scrollStart.current + delta;
  };

  const onMouseUp = () => {
    isDragging.current = false;
    if (trackRef.current) trackRef.current.style.cursor = 'grab';
  };

  const onTouchStart = (e) => {
    pausedRef.current = true;
    isDragging.current = true;
    startX.current = e.touches[0].pageX;
    scrollStart.current = trackRef.current.scrollLeft;
  };

  const onTouchMove = (e) => {
    const delta = startX.current - e.touches[0].pageX;
    trackRef.current.scrollLeft = scrollStart.current + delta;
  };

  const onTouchEnd = () => {
    isDragging.current = false;
    pausedRef.current = false;
  };

  const doubled = [...items, ...items];

  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="absolute left-0 top-0 bottom-4 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-4 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

      <div
        ref={trackRef}
        className="flex gap-6 pb-4 overflow-x-scroll"
        style={{ scrollbarWidth: 'none', cursor: 'grab', userSelect: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {doubled.map((property, idx) => (
          <div key={`${property.id}-${idx}`} className="flex-none w-[320px] sm:w-[360px]">
            <PropertyCard property={property} />
          </div>
        ))}
      </div>
    </div>
  );
}
